import { create } from 'zustand'
import apiHelper from '@/network/api'

interface Keyword {
  id: string
  keyword: string
  createdAt: string
  threadsUserId: string
  nextUpdateTime: string
}

interface KeywordListResponse {
  data: Keyword[]
  updateInterval: number
}

interface KeywordListState {
  keywords: Keyword[]
  updateInterval: number
  isLoading: boolean
  error: string | null
  
  // Actions
  fetchKeywords: () => Promise<void>
  setKeywords: (keywords: Keyword[]) => void
  createKeyword: (keyword: string, threadsUserId: string) => Promise<{
    success: boolean
    error?: number
  }>
  deleteKeyword: (id: string) => Promise<{
    success: boolean
    error?: string
  }>
}

const useKeywordListStore = create<KeywordListState>((set, get) => ({
  keywords: [],
  updateInterval: 3600,
  isLoading: false,
  error: null,

  fetchKeywords: async () => {
    try {
      set({ isLoading: true, error: null })
      const response = await apiHelper(null).get('keywords')
      
      if (response.status === 200) {
        const keywordData: KeywordListResponse = response.data
        set({ 
          keywords: keywordData.data,
          updateInterval: keywordData.updateInterval,
          isLoading: false 
        })
      }
    } catch (error) {
      set({ 
        error: 'Failed to fetch keywords',
        isLoading: false
      })
      console.error('Error fetching keywords:', error)
    }
  },

  createKeyword: async (keyword: string, threadsUserId: string) => {
    try {
      const response = await apiHelper(null).post('keyword/track', { keyword, threadsUserId })
      const data = response.data
      if (data) {
        // Refresh keywords list after successful creation
        await get().fetchKeywords()
        return { success: true }
      }
      return { success: false }
    } catch (error: any) {
      if (error.response?.data?.error) {
        return {
          success: false,
          error: error.response.data.error
        }
      }
      return { success: false }
    }
  },

  deleteKeyword: async (id: string) => {
    try {
      const response = await apiHelper(null).delete(`keywords/${id}`)
      if (response.status === 204) {
        // Refresh keywords list after successful deletion
        await get().fetchKeywords()
        return { success: true }
      }
      return { success: false }
    } catch (error) {
      if (error instanceof Error) {
        return {
          success: false,
          error: error.message
        }
      }
      return { success: false }
    }
  },

  setKeywords: (keywords: Keyword[]) => set({ keywords })
}))

export default useKeywordListStore
